import type { ContractType } from '@timelog/pm-shared';

import type { ContractStaffing } from './contractStaffing';

/**
 * How the current plan compares against the contract — the statements under
 * the task panel's contract KPIs and hours cascade (2026-11-02, the designer's
 * ask). Two readings of the same three numbers, for a fixed-price contract:
 *
 * - **factor** — the forecasted factor is what the current allocated cost
 *   (CALC-21) would yield on the Own Work amount budget. Either the target
 *   factor set on the contract is aligned to it, or the cost is brought to
 *   `costAtTarget` = own amount ÷ target to meet the target.
 * - **budget** — the forecasted amount (cost × target, `forecastedRevenueAtOwnFactor`)
 *   is the Own Work amount budget that holds the target at the current cost.
 *   The budget is aligned to it — by `delta` — to stick to the target.
 *
 * `direction` says which way each move goes: 'below' when the forecast sits
 * under the reference (cost too high for the target / budget too high for the
 * cost), 'above' when over. When the forecast meets the target within a
 * currency unit there is one statement: on target.
 *
 * Without an Own Work amount the factor cannot be read, but the budget can
 * (2026-11-02, the designer's ask): the forecasted amount is what the current
 * allocations call for — `noBudget` carries that one figure.
 *
 * Null when there is nothing to read: no allocated cost, no target, or a
 * contract priced by a rate card (its forecast is the lines' sell rates, not a
 * factor on the budget — the KPI boxes say so already).
 *
 * Pure so the figures are unit-testable in the `node` environment: import this
 * module, never the `planning` barrel.
 */
export type ContractPlanReading =
  | { kind: 'onTarget'; target: number; own: number }
  | { kind: 'noBudget'; target: number; cost: number; budget: { forecast: number } }
  | {
      kind: 'reading';
      /** The target factor set on the contract (CALC-20). */
      target: number;
      /** The contract's Own Work amount. */
      own: number;
      /** The mapped tasks' allocated cost, whole currency units. */
      cost: number;
      factor: {
        /** Own amount ÷ cost. */
        forecast: number;
        /** The cost that meets the target on the current own amount, whole units. */
        costAtTarget: number;
        direction: 'below' | 'above';
      };
      budget: {
        /** Cost × target: the own amount that holds the target, whole units. */
        forecast: number;
        /** |forecast − own|, whole units. */
        delta: number;
        direction: 'below' | 'above';
      };
    };

export const contractPlanReading = (
  staffing: Pick<ContractStaffing, 'allocatedCost' | 'ownAmount'>,
  type: ContractType,
  target: number | null,
): ContractPlanReading | null => {
  if (type !== 'fixed' || target === null || target <= 0 || staffing.allocatedCost <= 0) {
    return null;
  }
  const own = staffing.ownAmount;
  const cost = Math.round(staffing.allocatedCost);
  const forecastAmount = Math.round(staffing.allocatedCost * target);
  if (own <= 0) return { kind: 'noBudget', target, cost, budget: { forecast: forecastAmount } };
  const forecastFactor = own / staffing.allocatedCost;
  const delta = Math.abs(forecastAmount - own);
  if (delta < 1) return { kind: 'onTarget', target, own };
  return {
    kind: 'reading',
    target,
    own,
    cost,
    factor: {
      forecast: forecastFactor,
      costAtTarget: Math.round(own / target),
      direction: forecastFactor < target ? 'below' : 'above',
    },
    budget: {
      forecast: forecastAmount,
      delta,
      direction: forecastAmount < own ? 'below' : 'above',
    },
  };
};
